import { formSessionError, portalFormSessionError } from '@jukun/platform-sdk/core';
import { validateFormSessionReceiver, type PlatformFormSessionHostCapability, type PlatformFormSessionReceiver } from '@jukun/platform-sdk/shell';
import type { PortalApplication } from '../model/portalModels';

export type ApplicationFormRuntime = {
  readonly applicationCode: string; readonly authEpoch: string; readonly entryFingerprint: string;
  readonly runtimeEpoch: string; readonly runtimeName: string; readonly container: HTMLElement;
  readonly physicalAbort: AbortController; capability: PlatformFormSessionHostCapability;
  state: 'STARTING' | 'AVAILABLE' | 'PARKED' | 'DESTROYING' | 'BLOCKED';
  createPromise: Promise<ApplicationFormRuntime>; rawStart: Promise<void>; receiver?: PlatformFormSessionReceiver;
  receiverReady: Promise<PlatformFormSessionReceiver>; invalidated: boolean;
  reclaimPromise?: Promise<void>; receiverRegistration?: object; onReceiverLost?: () => void;
  onReclaimed?: () => void;
  displayLease?: string;
  resolveReceiver: (receiver: PlatformFormSessionReceiver) => void;
};
export type FormRuntimeFactory = {
  start: (record: ApplicationFormRuntime, application: PortalApplication) => Promise<void>;
  destroy: (record: ApplicationFormRuntime) => Promise<void>;
};

export class ApplicationFormRuntimeRegistry {
  private readonly records = new Map<string, ApplicationFormRuntime>();
  private parkingContainer?: HTMLElement;
  constructor(private readonly createContainer = () => document.createElement('div')) {}

  get(applicationCode: string) { return this.records.get(applicationCode); }
  values(): IterableIterator<ApplicationFormRuntime> { return this.records.values(); }

  ensure(application: PortalApplication, authEpoch: string, factory: FormRuntimeFactory): Promise<ApplicationFormRuntime> {
    const fingerprint = formRuntimeEntryFingerprint(application);
    const existing = this.records.get(application.appCode);
    if (existing) {
      if (existing.invalidated || ['DESTROYING', 'BLOCKED'].includes(existing.state)
          || existing.authEpoch !== authEpoch || existing.entryFingerprint !== fingerprint) {
        throw portalFormSessionError('PORTAL_FORM_RUNTIME_RECLAIM_BLOCKED');
      }
      return existing.createPromise;
    }
    const runtimeEpoch = crypto.randomUUID();
    const physicalAbort = new AbortController();
    let resolveReceiver!: ApplicationFormRuntime['resolveReceiver'];
    const receiverReady = new Promise<PlatformFormSessionReceiver>(resolve => { resolveReceiver = resolve; });
    let record!: ApplicationFormRuntime;
    const capability: PlatformFormSessionHostCapability = Object.freeze({
      protocolVersion: 1, runtimeEpoch,
      registerReceiver: receiver => {
        if (record.invalidated || record.physicalAbort.signal.aborted || !['STARTING', 'AVAILABLE', 'PARKED'].includes(record.state)) throw formSessionError('STALE');
        validateFormSessionReceiver(receiver);
        if (record.receiver) throw formSessionError('RECEIVER_DUPLICATE');
        const registration = {};
        record.receiver = receiver; record.receiverRegistration = registration;
        record.resolveReceiver(receiver);
        return () => {
          if (record.receiverRegistration !== registration) return;
          record.receiver = undefined; record.receiverRegistration = undefined;
          // A new waiter must wait for the next registration, not an already settled old promise.
          record.receiverReady = new Promise(resolve => { record.resolveReceiver = resolve; });
          record.onReceiverLost?.();
        };
      },
    });
    const rawStart = Promise.resolve().then(() => {
      if (record.invalidated) throw formSessionError('STALE');
      // Wujie's connectedCallback creates the ShadowRoot before template rendering.
      // A detached start container cannot be repaired by the later display lease.
      this.moveToParking(record);
      return factory.start(record, application);
    });
    void rawStart.catch(() => undefined);
    const createPromise = boundPhysicalStart(rawStart, physicalAbort.signal).then(() => {
      if (record.invalidated) throw formSessionError('STALE');
      record.state = 'PARKED';
      return record;
    }).catch(cause => {
      this.invalidateRecord(record, factory);
      throw cause;
    });
    void createPromise.catch(() => undefined);
    record = {
      applicationCode: application.appCode, authEpoch, entryFingerprint: fingerprint, runtimeEpoch,
      runtimeName: `PORTAL_FORM::${application.appCode}::${runtimeEpoch}`,
      container: this.createContainer(), physicalAbort, state: 'STARTING', invalidated: false,
      capability, receiverReady, resolveReceiver, rawStart, createPromise,
    };
    record.container.dataset.formRuntimeEpoch = runtimeEpoch;
    record.container.style.cssText = 'width:100%;height:100%;min-height:0;';
    // Publish the complete record and singleflight promise before executing user/Wujie code.
    this.records.set(application.appCode, record);
    return createPromise;
  }

  park(record: ApplicationFormRuntime, expectedLease?: string) {
    if (this.records.get(record.applicationCode) !== record || record.invalidated) return;
    if (expectedLease && record.displayLease !== expectedLease) return;
    record.displayLease = undefined;
    this.moveToParking(record);
    if (record.state === 'AVAILABLE') record.state = 'PARKED';
  }

  block(record: ApplicationFormRuntime) {
    if (this.records.get(record.applicationCode) !== record || record.invalidated) return;
    record.displayLease = undefined; record.state = 'BLOCKED';
    this.moveToParking(record);
  }

  attach(record: ApplicationFormRuntime, target: HTMLElement, lease?: string) {
    if (this.records.get(record.applicationCode) !== record || record.invalidated
        || !['AVAILABLE', 'PARKED'].includes(record.state)) throw formSessionError('STALE');
    if (!target.isConnected || target.ownerDocument !== record.container.ownerDocument) throw formSessionError('HOST_UNAVAILABLE');
    // Do not reparent an already displayed instance: even connected DOM moves
    // invoke custom-element disconnect callbacks. alive=true preserves Wujie's
    // app/ShadowRoot on the required parking/display moves.
    if (record.container.parentNode !== target) target.append(record.container);
    record.displayLease = lease; record.state = 'AVAILABLE';
  }

  invalidate(applicationCode: string, factory: FormRuntimeFactory) {
    const record = this.records.get(applicationCode);
    if (record) this.invalidateRecord(record, factory);
  }

  invalidateAll(factory: FormRuntimeFactory) { for (const record of this.records.values()) this.invalidateRecord(record, factory); }

  private invalidateRecord(record: ApplicationFormRuntime, factory: FormRuntimeFactory) {
    if (record.invalidated) return;
    record.invalidated = true; record.state = 'DESTROYING'; record.displayLease = undefined;
    record.physicalAbort.abort(); record.receiver = undefined; record.receiverRegistration = undefined;
    this.moveToParking(record);
    record.onReceiverLost?.();
    // Keep ownership of the original start. Destroying before its late completion cannot prove reclamation.
    record.reclaimPromise = record.rawStart.catch(() => undefined).then(() => factory.destroy(record)).then(() => {
      record.container.remove();
      if (this.records.get(record.applicationCode) === record) this.records.delete(record.applicationCode);
      this.removeEmptyParkingContainer();
      record.onReclaimed?.(); record.onReclaimed = undefined; record.onReceiverLost = undefined;
    }).catch(() => { record.state = 'BLOCKED'; });
    void record.reclaimPromise.catch(() => undefined);
    const timer = setTimeout(() => {
      if (this.records.get(record.applicationCode) === record) record.state = 'BLOCKED';
    }, 2_000);
    void record.reclaimPromise.finally(() => clearTimeout(timer));
  }

  private moveToParking(record: ApplicationFormRuntime) {
    const ownerDocument = record.container.ownerDocument;
    if (!this.parkingContainer) {
      const parking = ownerDocument.createElement('div');
      parking.dataset.portalFormParking = '';
      parking.setAttribute('aria-hidden', 'true');
      parking.inert = true;
      // Keep layout dimensions available during initialization, without exposing
      // parked forms to pointer, keyboard or accessibility navigation.
      parking.style.cssText = 'position:fixed;inset:0;opacity:0;visibility:hidden;pointer-events:none;z-index:-1;overflow:hidden;';
      this.parkingContainer = parking;
    }
    const parking = this.parkingContainer;
    if (!parking.isConnected) ownerDocument.body.append(parking);
    if (record.container.parentNode !== parking) parking.append(record.container);
  }

  private removeEmptyParkingContainer() {
    if (!this.parkingContainer || this.parkingContainer.childNodes.length !== 0) return;
    this.parkingContainer.remove();
    this.parkingContainer = undefined;
  }
}

export function formRuntimeEntryFingerprint(application: PortalApplication) {
  return JSON.stringify([application.status, application.entry.embedUrl, application.entry.basePath, application.entry.defaultRoute]);
}

async function boundPhysicalStart(raw: Promise<void>, signal: AbortSignal) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let removeAbort: () => void = () => undefined;
  const stopped = new Promise<never>((_, reject) => {
    const abort = () => reject(formSessionError('STALE'));
    if (signal.aborted) return abort();
    signal.addEventListener('abort', abort, { once: true });
    removeAbort = () => signal.removeEventListener('abort', abort);
    timer = setTimeout(() => reject(formSessionError('OPEN_TIMEOUT')), 10_000);
  });
  try { await Promise.race([raw, stopped]); }
  finally { if (timer !== undefined) clearTimeout(timer); removeAbort(); }
}
